import React from "react";
import { Link } from "react-router-dom";
import { Users, Fuel, Gauge, MapPin, Truck, Star } from "lucide-react";
import { formatPrice } from "@/lib/currency";
import { fileUrl } from "@/lib/api";
import DealCountdown from "@/components/DealCountdown";

const FALLBACK = "https://images.unsplash.com/photo-1503376780353-7e6692767b70?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function CarCard({ car }) {
  const img = car.image_url || car.images?.[0];
  return (
    <Link
      to={`/cars/${car.id}`}
      data-testid={`car-card-${car.id}`}
      className="group glass glass-hover rounded-2xl overflow-hidden flex flex-col"
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-[#0B0E1B]">
        <img
          src={img ? fileUrl(img) : FALLBACK}
          alt={car.name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          onError={(e) => { e.currentTarget.src = FALLBACK; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#070A18] via-transparent to-transparent opacity-70" />
        <span className="absolute top-3 left-3 text-[10px] font-mono-caption uppercase tracking-widest px-3 py-1 rounded-full glass border text-cyan-300 border-cyan-300/30">
          {car.car_type}
        </span>
        <div className="absolute top-3 right-3 flex flex-col items-end gap-1.5">
          {car.deal_of_day && (
            <span className="text-[10px] font-mono-caption uppercase tracking-widest px-3 py-1 rounded-full bg-gradient-to-r from-[#ff5e00] to-[#d900ff] text-white font-bold shadow-lg" data-testid={`car-deal-${car.id}`}>
              Deal of the Day
            </span>
          )}
          {car.delivery_available && (
            <span className="text-[10px] font-mono-caption uppercase tracking-widest px-3 py-1 rounded-full glass border text-emerald-400 border-emerald-400/30 flex items-center gap-1">
              <Truck className="w-3 h-3" /> Delivery
            </span>
          )}
        </div>
        {car.images && car.images.length > 1 && (
          <span className="absolute bottom-3 right-3 text-[10px] font-mono-caption px-2 py-1 rounded-full glass text-white">
            {car.images.length} photos
          </span>
        )}
        <div className="absolute bottom-3 left-3 flex items-center gap-1.5 glass rounded-full px-3 py-1" data-testid={`car-rating-${car.id}`}>
          <Star className="w-3.5 h-3.5 fill-[#22E67A] text-[#22E67A]" />
          {car.reviews_count > 0 ? (
            <>
              <span className="text-white text-xs font-bold">{car.rating?.toFixed(1)}</span>
              <span className="text-slate-400 text-[11px]">({car.reviews_count})</span>
            </>
          ) : (
            <span className="text-white text-xs font-bold">New</span>
          )}
        </div>
      </div>

      <div className="p-5 flex flex-col flex-1">
        <h3 className="font-heading font-extrabold text-white text-xl leading-tight mb-3 group-hover:text-gradient-cool transition-colors line-clamp-1">
          {car.name}
        </h3>
        {car.deal_of_day && <DealCountdown testid={`car-countdown-${car.id}`} className="mb-3" />}
        <div className="grid grid-cols-2 gap-y-1.5 gap-x-3 mb-4 flex-1">
          <span className="flex items-center gap-1.5 text-xs text-slate-400"><Users className="w-3.5 h-3.5 text-cyan-300 shrink-0" /> {car.passenger_capacity} seats</span>
          <span className="flex items-center gap-1.5 text-xs text-slate-400"><Gauge className="w-3.5 h-3.5 text-orange-400 shrink-0" /> {car.transmission || "Automatic"}</span>
          <span className="flex items-center gap-1.5 text-xs text-slate-400"><Fuel className="w-3.5 h-3.5 text-fuchsia-400 shrink-0" /> {car.fuel_policy}</span>
          <span className="flex items-center gap-1.5 text-xs text-slate-400"><MapPin className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> {car.island}</span>
        </div>
        <div className="flex items-end justify-between pt-3 border-t border-[#232A4D]">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono-caption">Per day</div>
          <div className="text-right">
            {car.deal_of_day && (
              <div className="text-slate-500 text-xs line-through">{formatPrice(car.original_price_per_day, car.currency)}</div>
            )}
            <span className="font-heading font-extrabold price-red text-2xl leading-none">{formatPrice(car.price_per_day, car.currency)}</span>
            <div className="text-slate-500 text-[11px]">/ day</div>
          </div>
        </div>
      </div>
    </Link>
  );
}
